import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTranslation } from 'react-i18next';
import './HorizonFeaturesTab.css';
const IMG = '/images/atmosair';
/**
 * Ansichten von AtmosAir – Bild + Erklärung je Screen.
 * Bilder liegen unter public/images/atmosair/ (vom Deployer bereitgestellt).
 */
const AirQualityViewsTab = () => {
    const { t } = useTranslation();
    const views = [
        {
            id: 'current',
            image: `${IMG}/currentview.png`,
            imagePosition: 'left',
            title: t('aqpro.views.current.title', 'Current air quality'),
            items: [
                t('aqpro.views.current.gauge', 'Large coloured gauge: current AQI (EU or US, depending on your setting). The marker shows where you are on the scale.'),
                t('aqpro.views.current.category', 'Category label (e.g. Good / Moderate / Poor) under or next to the value.'),
                t('aqpro.views.current.pollutants', 'PM2.5, PM10 and O₃ values are shown as quick reference.'),
                t('aqpro.views.current.location', 'Location name and how long ago the data was updated.'),
            ],
        },
        {
            id: 'hourly',
            image: `${IMG}/24hforecastview.png`,
            imagePosition: 'right',
            title: t('aqpro.views.hourly.title', '24-hour forecast'),
            items: [
                t('aqpro.views.hourly.bars', 'Bar chart of AQI for the next 24 hours, starting from the current hour.'),
                t('aqpro.views.hourly.colors', 'Bar colours follow the same AQI categories as the main gauge.'),
                t('aqpro.views.hourly.axis', 'Time runs left → right (now → later).'),
            ],
        },
        {
            id: 'daily',
            image: `${IMG}/dailyforecastview.png`,
            imagePosition: 'left',
            title: t('aqpro.views.daily.title', '5-day forecast'),
            items: [
                t('aqpro.views.daily.range', 'Each day shows the expected AQI maximum and minimum.'),
                t('aqpro.views.daily.use', 'Useful for planning outdoor activities over the coming days.'),
            ],
        },
        {
            id: 'dust',
            image: `${IMG}/dustview.png`,
            imagePosition: 'right',
            title: t('aqpro.views.dust.title', 'Saharan dust / dust'),
            items: [
                t('aqpro.views.dust.current', 'Current dust level plus a 24-hour trend.'),
                t('aqpro.views.dust.daily', 'Multi-day dust outlook when data is available.'),
                t('aqpro.views.dust.note', 'Especially relevant during Saharan dust events that raise PM levels.'),
            ],
        },
        {
            id: 'particles',
            image: `${IMG}/particleforecastview.png`,
            imagePosition: 'left',
            title: t('aqpro.views.particles.title', 'Particle comparison'),
            items: [
                t('aqpro.views.particles.dual', 'Dual chart comparing two series you choose in settings (e.g. PM2.5 vs PM10, or O₃ vs NO₂).'),
                t('aqpro.views.particles.single', 'If only one series is selected, it uses the full width.'),
                t('aqpro.views.particles.series', 'Available series: PM2.5, PM10, Dust, UV, O₃, NO₂, CO, SO₂, AOD.'),
            ],
        },
        {
            id: 'visibility',
            image: `${IMG}/visibilityview.png`,
            imagePosition: 'right',
            title: t('aqpro.views.visibility.title', 'Haze / visibility (AOD)'),
            items: [
                t('aqpro.views.visibility.aod', 'Based on Aerosol Optical Depth (AOD) – how much aerosols dim the atmosphere.'),
                t('aqpro.views.visibility.km', 'Converted into an approximate visibility in kilometres.'),
                t('aqpro.views.visibility.chart', '24-hour chart: taller bars ≈ better visibility; colour indicates haze density.'),
            ],
        },
        {
            id: 'pollen',
            image: `${IMG}/pollenview.png`,
            imagePosition: 'left',
            title: t('aqpro.views.pollen.title', 'Pollen (Europe)'),
            items: [
                t('aqpro.views.pollen.species', 'Six types: alder, birch, grass, mugwort, olive, ragweed – each with a level bar.'),
                t('aqpro.views.pollen.chart', 'Bottom chart: 24-hour maximum across all types.'),
                t('aqpro.views.pollen.europe', 'Data comes from CAMS and is typically available in Europe only. Outside Europe you may see “No data”.'),
            ],
        },
        {
            id: 'glance',
            image: `${IMG}/glanceview.png`,
            imagePosition: 'right',
            title: t('aqpro.views.glance.title', 'Glance view'),
            items: [
                t('aqpro.views.glance.what', 'Compact watch face glance: current AQI value, category and a colour scale with marker.'),
                t('aqpro.views.glance.mono', 'On monochrome Instinct watches a progress bar is used instead of the colour scale.'),
            ],
        },
    ];
    return (_jsxs("div", { className: "horizon-features-tab", children: [_jsx("p", { style: { color: 'var(--text-secondary)', margin: '0 0 1rem', fontSize: '0.95rem', lineHeight: 1.55 }, children: t('aqpro.views.intro', 'Atmos Air Quality shows current air quality and forecasts as a Garmin widget. Swipe or use Up/Down to move between the pages you enabled in settings.') }), views.map((view) => (_jsxs("div", { className: `horizon-feature-row ${view.imagePosition === 'right' ? 'image-right' : 'image-left'}`, children: [_jsx("div", { className: "horizon-feature-image", children: _jsx("img", { src: view.image, alt: view.title }) }), _jsxs("div", { className: "horizon-feature-text", children: [_jsx("h3", { children: view.title }), _jsx("ul", { children: view.items.map((item, idx) => (_jsx("li", { children: item }, idx))) })] })] }, view.id)))] }));
};
export default AirQualityViewsTab;
